import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { Icon, IconName } from '@vio/atlas/Icon';
import { facilityIconsById } from 'components/Accommodation/Facilities/FacilityIcon';
import { getFacets } from 'modules/sapiSearch/selectors';
import { getIsCheckInAtLeastTwoDaysAway, getListOfValues } from 'modules/search/selectors';
import { Lov } from 'modules/search/slice';
import { FilterUrlParams } from 'modules/search/types';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { FacetCard } from '../FacetFilter/FacetCard';
import { filterMessages } from '../filterMessages';
import { getFiltersLabel, getOrderedActiveQuickFilters, IQuickFilter, isFacetValid } from '../utils';
import type { FacetsAsArray } from '@daedalus/core/src/sapi/types';
const PopularFiltersWrapper = /*#__PURE__*/styled('div')({
  name: "PopularFiltersWrapper",
  class: "pvp638r",
  propsAsIs: false
});

/**
 * List of ranked popular filters
 */
const POPULAR_FILTERS = {
  freeCancellation: '1',
  reviewScore: '8',
  starRatings: ['4', '5'],
  facilities: ['7',
  // Swimming Pool
  '6',
  // Parking
  '8' // Pet Friendly
  ],
  themes: ['1',
  // Beach / Coast
  '9' // Family
  ],
  fallbackFacilities: ['299',
  // Free Wifi
  '5',
  // Restaurant
  '21',
  // Air conditioned
  '9' // Airport shuttle
  ]
};
export const themeIconsById: Record<number, IconName> = {
  1: 'Beach2',
  9: 'Person'
};
interface PopularFilterProps {
  isActive?: boolean;
  handleSingleFilterChange: (value: FilterUrlParams) => void;
  listOfValues: Lov;
  toggleMultiFilter: (filterKey: keyof FilterUrlParams, filterValue: string | null | undefined) => void;
  facets: FacetsAsArray;
}
export type PopularFilter = Omit<IQuickFilter<PopularFilterProps>, 'id'> & {
  id: string;
};
const POPULAR_FILTERS_LIST: PopularFilter[] = [{
  id: 'FreeCancellation',
  name: 'freeCancellation',
  Component: ({
    isActive,
    handleSingleFilterChange
  }: PopularFilterProps) => <PopularFilterItem isActive={isActive} onChange={() => handleSingleFilterChange({
    freeCancellation: isActive ? undefined : '1'
  })} iconName="CalendarCheck4">
        <FormattedMessageWrapper {...filterMessages.freeCancellationTitle} />
      </PopularFilterItem>
}, {
  id: 'StarRating',
  name: 'starRatings',
  activeCondition: {
    name: 'starRatings',
    value: ['4', '5']
  },
  Component: ({
    isActive,
    handleSingleFilterChange
  }: PopularFilterProps) => <PopularFilterItem isActive={isActive} onChange={() => handleSingleFilterChange({
    starRatings: isActive ? undefined : ['4', '5']
  })} iconName="Star">
        <FormattedMessageWrapper id="filters.starRatingSelectedTo" defaultMessage="{starRating1} to {starRating2}-star" values={{
      starRating1: 4,
      starRating2: 5
    }} />
      </PopularFilterItem>
}, {
  id: 'ReviewScore',
  name: 'reviewScore',
  activeCondition: {
    name: 'reviewScore',
    value: '8'
  },
  Component: ({
    isActive,
    handleSingleFilterChange
  }: PopularFilterProps) => <PopularFilterItem isActive={isActive} onChange={() => handleSingleFilterChange({
    reviewScore: isActive ? undefined : '8'
  })} iconName="SpeechBubble">
        <FormattedMessageWrapper id="guestRatingsWithValue" defaultMessage="{value}+ rating" values={{
      value: 8
    }} />
      </PopularFilterItem>
}, ...POPULAR_FILTERS.facilities.map(facilityId => ({
  id: `FacilitiesFilter:${facilityId}`,
  name: `facility_${facilityId}`,
  activeCondition: {
    name: 'facilities',
    value: facilityId
  },
  Component: ({
    isActive,
    toggleMultiFilter,
    listOfValues
  }: PopularFilterProps) => <PopularFilterItem isActive={isActive} onChange={() => toggleMultiFilter('facilities', facilityId)} iconName={facilityIconsById[facilityId]}>
        {getFiltersLabel('Facility', facilityId, listOfValues)}
      </PopularFilterItem>
})), ...POPULAR_FILTERS.themes.map(themeId => ({
  id: `ThemesFilter:${themeId}`,
  name: `theme_${themeId}`,
  activeCondition: {
    name: 'themes',
    value: themeId
  },
  Component: ({
    isActive,
    toggleMultiFilter,
    listOfValues
  }: PopularFilterProps) => <PopularFilterItem isActive={isActive} onChange={() => toggleMultiFilter('themes', themeId)} iconName={themeIconsById[themeId]}>
        {getFiltersLabel('Theme', themeId, listOfValues)}
      </PopularFilterItem>
})), ...POPULAR_FILTERS.fallbackFacilities.map(facilityId => ({
  id: `FacilitiesFilter:${facilityId}`,
  name: `facility_${facilityId}`,
  activeCondition: {
    name: 'facilities',
    value: facilityId
  },
  Component: ({
    isActive,
    toggleMultiFilter,
    listOfValues
  }: PopularFilterProps) => <PopularFilterItem isActive={isActive} onChange={() => toggleMultiFilter('facilities', facilityId)} iconName={facilityIconsById[facilityId]}>
        {getFiltersLabel('Facility', facilityId, listOfValues)}
      </PopularFilterItem>
}))];
interface Props {
  filters: FilterUrlParams;
  handleSingleFilterChange: (value: FilterUrlParams) => void;
  toggleMultiFilter: (filterKey: keyof FilterUrlParams, filterValue: string | null | undefined) => void;
}
const PopularFilterItem: React.FC<{
  isActive: boolean;
  onChange: () => void;
  iconName: IconName;
}> = ({
  onChange,
  isActive,
  iconName,
  children
}) => {
  return <FacetCard icon={<Icon name={iconName} size="lg" colorPath="content.neutral.c600" />} label={children} isSelected={isActive} onSelectCard={onChange} />;
};
export const PopularFilters: React.FC<Props> = ({
  filters,
  handleSingleFilterChange,
  toggleMultiFilter
}) => {
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const listOfValues = useSelector(getListOfValues);
  const facets = useSelector(getFacets);
  const isCheckInAtLeastTwoDaysAway = useSelector(getIsCheckInAtLeastTwoDaysAway);
  const isValidFilter = ({
    name
  }: IQuickFilter) => {
    if (Array.isArray(name)) {
      return true;
    }
    if (name === 'freeCancellation' && !isCheckInAtLeastTwoDaysAway) {
      return false;
    }
    if (name.startsWith('facility_') || name.startsWith('theme_')) {
      const [filterType, filterId] = name.split('_');
      return isFacetValid(filterId, filterType === 'facility' ? 'facilities' : 'themeIds', facets);
    }
    return true;
  };
  useEffect(() => {
    setActiveFilters(prevFilters => getOrderedActiveQuickFilters(POPULAR_FILTERS_LIST, filters, prevFilters));
  }, [filters]);
  return <PopularFiltersWrapper>
      {POPULAR_FILTERS_LIST.filter(isValidFilter).slice(0, 8).map(({
      id,
      Component
    }) => <Component key={id} isActive={activeFilters.includes(id)} toggleMultiFilter={toggleMultiFilter} handleSingleFilterChange={handleSingleFilterChange} listOfValues={listOfValues} facets={facets} />)}
    </PopularFiltersWrapper>;
};

require("./PopularFilters.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./PopularFilters.tsx");